import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { finalize, forkJoin } from 'rxjs';
import { TableModule, TableLazyLoadEvent } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { TagModule } from 'primeng/tag';
import { CheckboxModule } from 'primeng/checkbox';
import { TooltipModule } from 'primeng/tooltip';
import { TextareaModule } from 'primeng/textarea';
import { DatePickerModule } from 'primeng/datepicker';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { AuthService } from '../../core/services/auth.service';
import { InventoryService, StockHistoryFilter } from '../../core/services/inventory.service';
import { ProductService } from '../../core/services/product.service';
import { StoreService } from '../../core/services/store.service';
import {
  ProductLiteDto,
  StockHistoryItem,
  WriteOffRequest,
} from '../../core/models/product.models';
import { ProductVariantDto } from '../../core/models/variant.models';
import { StoreLiteDto } from '../../core/models/store.models';
import { storePicker } from '../../core/utils/store-picker';
import { BarcodeScannerComponent } from '../../shared/components/barcode-scanner/barcode-scanner.component';
import { MoneyPipe } from '../../shared/pipes/money.pipe';

@Component({
  selector: 'app-write-off',
  standalone: true,
  imports: [
    MoneyPipe,
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    TagModule,
    CheckboxModule,
    TooltipModule,
    TextareaModule,
    DatePickerModule,
    BarcodeScannerComponent,
    TranslatePipe,
    MultiSelectModule,
  ],
  templateUrl: './write-off.component.html',
})
export class WriteOffComponent implements OnInit {
  private inventorySvc = inject(InventoryService);
  private productSvc = inject(ProductService);
  private storeSvc = inject(StoreService);
  private translate = inject(TranslateService);
  private authSvc = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);

  // ── Lookup data ──────────────────────────────────────────────────────────────
  stores = signal<StoreLiteDto[]>([]);
  allProducts = signal<ProductLiteDto[]>([]);
  /** Mahsulotlar ro'yxati hali kelmadimi — tanlagichlarda spinner ko'rsatiladi. */
  productsLoading = signal(false);

  /** Bitta do'konli biznesda tanlagichlar yashiriladi — `storePicker` ga qarang. */
  readonly storePick = storePicker(this.stores);

  /** Til almashganda yorliqlar yangilansin — `computed` ni shu signal qayta hisoblatadi. */
  private readonly lang = toSignal(this.translate.onLangChange, { initialValue: null });

  /**
   * `computed`, getter emas: getter har bir change detection siklida YANGI massiv
   * qaytarardi va `p-select` uni har safar o'zgargan deb qayta ishlab chiqardi
   * (ustiga 9 ta `translate.instant`). Dialog animatsiyasi paytida bu sezilarli
   * sekinlashuv berardi.
   */
  readonly stockTypes = computed(() => {
    this.lang();
    return [
      { label: this.translate.instant('WRITEOFF.STOCK_TYPES.ALL'), value: null },
      { label: this.translate.instant('WRITEOFF.STOCK_TYPES.STOCK_IN'), value: 1 },
      { label: this.translate.instant('WRITEOFF.STOCK_TYPES.SALE'), value: 2 },
      { label: this.translate.instant('WRITEOFF.STOCK_TYPES.ADJUSTMENT'), value: 3 },
      { label: this.translate.instant('WRITEOFF.STOCK_TYPES.TRANSFER_OUT'), value: 4 },
      { label: this.translate.instant('WRITEOFF.STOCK_TYPES.TRANSFER_IN'), value: 5 },
      { label: this.translate.instant('WRITEOFF.STOCK_TYPES.WRITE_OFF'), value: 6 },
      { label: this.translate.instant('WRITEOFF.STOCK_TYPES.RETURN'), value: 7 },
      { label: this.translate.instant('WRITEOFF.STOCK_TYPES.DEFECTIVE_RETURN'), value: 8 },
    ];
  });

  // ── History table ────────────────────────────────────────────────────────────
  historyItems = signal<StockHistoryItem[]>([]);
  totalCount = signal(0);
  loading = signal(false);
  pageSize = 50;

  // ── Filters ──────────────────────────────────────────────────────────────────
  filterProductId: string | null = null;
  filterStoreIds: string[] = [];
  filterType: number | null = null;
  filterFrom: Date | null = null;
  filterTo: Date | null = null;

  private lastEvent: TableLazyLoadEvent = { first: 0, rows: this.pageSize };
  private storesLoaded = false;

  // ── Write-off dialog ─────────────────────────────────────────────────────────
  dialogVisible = false;
  saving = false;
  form: {
    productId: string | null;
    storeId: string | null;
    quantity: number;
    description: string;
    productVariantId: string | null;
    fromDefective: boolean;
  } = this.emptyForm();

  /** Variantsiz (oddiy) mahsulot uchun default variant brak qoldig'i. */
  simpleDefective = signal<number>(0);

  // ── Variant tanlash (variantli mahsulot uchun) ─────────────────────────────────
  variants = signal<ProductVariantDto[]>([]);
  variantsLoading = signal(false);
  /** Mahsulotda haqiqiy variant (atributli) bormi — bo'lsa tanlash majburiy. */
  hasRealVariants = signal(false);

  /** `computed` — shablondan chaqiriladigan metod har CD siklida yangi massiv berardi. */
  readonly variantOptions = computed(() =>
    this.variants().map((v) => ({
      id: v.id,
      label: this.variantLabel(v),
      stock: v.stock ?? 0,
      defectiveStock: v.defectiveStock ?? 0,
    })),
  );

  /** Joriy tanlovga mos brak qoldig'i (chiqim oynasi uchun). */
  defectiveAvailable(): number {
    if (this.hasRealVariants()) {
      const v = this.variants().find((x) => x.id === this.form.productVariantId);
      return v?.defectiveStock ?? 0;
    }
    return this.simpleDefective();
  }

  variantLabel(v: ProductVariantDto): string {
    const label = v.values.map((x) => x.value).join(' / ');
    return label || v.sku || '—';
  }

  ngOnInit() {
    // Default: bugungi kun (from = kun boshi, to = kun oxiri) — foydalanuvchi UI'dan o'zgartiradi
    const today = new Date();
    this.filterFrom = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
    this.filterTo = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      23,
      59,
      59,
      999,
    );

    this.productsLoading.set(true);
    forkJoin({
      stores: this.storeSvc.getAllLite(),
      products: this.productSvc.getAllLite(),
    })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.productsLoading.set(false)),
      )
      .subscribe({
        next: ({ stores, products }) => {
          const storeList = stores.data ?? [];
          this.stores.set(storeList);
          this.allProducts.set(products.data ?? []);
          if (this.authSvc.user()?.role === 'Employee' && storeList.length > 0) {
            this.filterStoreIds = [storeList[0].id];
          }
          this.storesLoaded = true;
          this.loadHistory(this.lastEvent);
        },
      });
  }

  loadHistory(event: TableLazyLoadEvent) {
    this.lastEvent = event;
    if (!this.storesLoaded) return;
    const page = Math.floor((event.first ?? 0) / (event.rows ?? this.pageSize)) + 1;
    const filter: StockHistoryFilter = {
      page,
      pageSize: event.rows ?? this.pageSize,
      productId: this.filterProductId ?? undefined,
      storeIds: this.filterStoreIds.length ? this.filterStoreIds : undefined,
      type: this.filterType ?? undefined,
      fromDate: this.toIso(this.filterFrom),
      toDate: this.toIso(this.filterTo),
    };
    this.loading.set(true);
    this.inventorySvc
      .getHistory(filter)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.historyItems.set(res.data?.items ?? []);
          this.totalCount.set(res.data?.totalCount ?? 0);
          this.loading.set(false);
        },
        error: () => this.loading.set(false),
      });
  }

  applyFilters() {
    this.loadHistory({ first: 0, rows: this.pageSize });
  }

  // ── Write-off dialog ─────────────────────────────────────────────────────────

  openDialog() {
    this.form = this.emptyForm();
    this.variants.set([]);
    this.hasRealVariants.set(false);
    // Do'kon bitta bo'lsa tanlagich ko'rsatilmaydi — qiymatni shu yerda qo'yamiz.
    this.form.storeId = this.storePick.single()?.id ?? null;
    this.dialogVisible = true;
  }

  /** Mahsulot yoki do'kon o'zgarganda variant matritsasini (qoldiq bilan) qayta yuklaymiz. */
  onWriteOffProductChange(productId: string | null) {
    this.form.productId = productId;
    this.form.productVariantId = null;
    this.loadVariants();
  }

  onWriteOffStoreChange(storeId: string | null) {
    this.form.storeId = storeId;
    this.loadVariants();
  }

  private loadVariants() {
    const pid = this.form.productId;
    if (!pid) {
      this.variants.set([]);
      this.hasRealVariants.set(false);
      return;
    }
    this.variantsLoading.set(true);
    this.productSvc
      .getVariantMatrix(pid, this.form.storeId ?? undefined)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          const m = res.data;
          const hasAxes = !!m && m.attributes.length > 0 && m.variants.length > 0;
          this.hasRealVariants.set(hasAxes);
          this.variants.set(hasAxes ? m!.variants : []);
          // Oddiy mahsulotda default variant brak qoldig'ini saqlaymiz (chiqim uchun)
          this.simpleDefective.set(
            !hasAxes && m && m.variants.length > 0 ? (m.variants[0].defectiveStock ?? 0) : 0,
          );
          // Avval tanlangan variant endi ro'yxatda bo'lmasa — tozalaymiz
          if (
            this.form.productVariantId &&
            !this.variants().some((v) => v.id === this.form.productVariantId)
          ) {
            this.form.productVariantId = null;
          }
          this.variantsLoading.set(false);
        },
        error: () => {
          this.variants.set([]);
          this.hasRealVariants.set(false);
          this.variantsLoading.set(false);
        },
      });
  }

  onFilterBarcodeDetected(barcode: string) {
    const found = this.allProducts().find((p) => p.barcode === barcode);
    if (found) {
      this.filterProductId = found.id;
      this.inventorySvc.showSuccess(
        this.translate.instant('WRITEOFF.TOAST.FOUND', { name: found.name }),
      );
    } else {
      this.productSvc
        .getByBarcode(barcode)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (p) => {
            if (p) {
              this.filterProductId = p.id;
              if (!this.allProducts().find((x) => x.id === p.id))
                this.allProducts.update((list) => [
                  {
                    id: p.id,
                    name: p.name,
                    barcode: p.barcode,
                    storeTypeId: null,
                    hasVariants: p.hasVariants ?? false,
                    purchasePrice: p.purchasePrice,
                    sellPrice: p.sellPrice,
                    sellCurrency: p.sellCurrency,
                    purchaseCurrency: p.purchaseCurrency,
                  },
                  ...list,
                ]);
              this.inventorySvc.showSuccess(
                this.translate.instant('WRITEOFF.TOAST.FOUND', { name: p.name }),
              );
            } else {
              this.inventorySvc.showWarn(
                this.translate.instant('WRITEOFF.TOAST.NOT_FOUND', { barcode }),
              );
            }
          },
        });
    }
  }

  onBarcodeDetected(barcode: string) {
    const found = this.allProducts().find((p) => p.barcode === barcode);
    if (found) {
      this.onWriteOffProductChange(found.id);
      this.inventorySvc.showSuccess(
        this.translate.instant('WRITEOFF.TOAST.FOUND', { name: found.name }),
      );
    } else {
      this.productSvc
        .getByBarcode(barcode)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (p) => {
            if (p) {
              this.onWriteOffProductChange(p.id);
              if (!this.allProducts().find((x) => x.id === p.id))
                this.allProducts.update((list) => [
                  {
                    id: p.id,
                    name: p.name,
                    barcode: p.barcode,
                    storeTypeId: null,
                    hasVariants: p.hasVariants ?? false,
                    purchasePrice: p.purchasePrice,
                    sellPrice: p.sellPrice,
                    sellCurrency: p.sellCurrency,
                    purchaseCurrency: p.purchaseCurrency,
                  },
                  ...list,
                ]);
              this.inventorySvc.showSuccess(
                this.translate.instant('WRITEOFF.TOAST.FOUND', { name: p.name }),
              );
            } else {
              this.inventorySvc.showWarn(
                this.translate.instant('WRITEOFF.TOAST.NOT_FOUND', { barcode }),
              );
            }
          },
        });
    }
  }

  saveWriteOff() {
    // Tanlagich yashirilgan bo'lsa qiymat forma ochilishida qo'yiladi. Ro'yxat
    // forma ochilgandan KEYIN kelgan bo'lsa esa — shu yerda: aks holda maydon
    // ko'zdan g'oyib bo'lar, qiymat bo'sh qolar va xato tuzatib bo'lmasdi.
    this.form.storeId ??= this.storePick.single()?.id ?? null;

    if (!this.form.productId || !this.form.storeId || this.form.quantity <= 0) {
      this.inventorySvc.showWarn(this.translate.instant('WRITEOFF.TOAST.VALIDATION'));
      return;
    }
    // Variantli mahsulotda variant tanlanishi shart — aks holda noto'g'ri variant yechiladi.
    if (this.hasRealVariants() && !this.form.productVariantId) {
      this.inventorySvc.showWarn(this.translate.instant('WRITEOFF.TOAST.SELECT_VARIANT'));
      return;
    }
    // Brak ombordan chiqarishda mavjud brak qoldig'idan oshmasligi kerak.
    if (this.form.fromDefective && this.form.quantity > this.defectiveAvailable()) {
      this.inventorySvc.showWarn(
        this.translate.instant('WRITEOFF.TOAST.INSUFFICIENT_DEFECTIVE', {
          available: this.defectiveAvailable(),
        }),
      );
      return;
    }
    this.saving = true;
    const req: WriteOffRequest = {
      productId: this.form.productId,
      storeId: this.form.storeId,
      quantity: this.form.quantity,
      description: this.form.description,
      productVariantId: this.form.productVariantId,
      fromDefective: this.form.fromDefective,
    };
    this.inventorySvc
      .writeOff(req)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.inventorySvc.showSuccess(this.translate.instant('WRITEOFF.TOAST.SAVE_SUCCESS'));
          this.dialogVisible = false;
          this.loadHistory({ first: 0, rows: this.pageSize });
        },
        error: () => {
          this.saving = false;
        },
        complete: () => {
          this.saving = false;
        },
      });
  }

  // ── Helpers ───────────────────────────────────────────────────────────────────

  typeSeverity(type: number): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' {
    const map: Record<number, 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast'> = {
      1: 'success',
      2: 'info',
      3: 'warn',
      4: 'secondary',
      5: 'contrast',
      6: 'danger',
      7: 'success',
      8: 'warn',
    };
    return map[type] ?? 'secondary';
  }

  typeLabel(type: number): string {
    const map: Record<number, string> = {
      1: this.translate.instant('WRITEOFF.TYPE_LABELS.STOCK_IN'),
      2: this.translate.instant('WRITEOFF.TYPE_LABELS.SALE'),
      3: this.translate.instant('WRITEOFF.TYPE_LABELS.ADJUSTMENT'),
      4: this.translate.instant('WRITEOFF.TYPE_LABELS.TRANSFER_OUT'),
      5: this.translate.instant('WRITEOFF.TYPE_LABELS.TRANSFER_IN'),
      6: this.translate.instant('WRITEOFF.TYPE_LABELS.WRITE_OFF'),
      7: this.translate.instant('WRITEOFF.TYPE_LABELS.RETURN'),
      8: this.translate.instant('WRITEOFF.TYPE_LABELS.DEFECTIVE_RETURN'),
    };
    return map[type] ?? this.translate.instant('WRITEOFF.TYPE_LABELS.UNKNOWN');
  }

  formatDate(iso: string): string {
    return new Date(iso).toLocaleString('uz-UZ', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  private toIso(date: Date | null): string | undefined {
    return date ? date.toISOString() : undefined;
  }

  private emptyForm() {
    return {
      productId: null as string | null,
      storeId: null as string | null,
      quantity: 1,
      description: '',
      productVariantId: null as string | null,
      fromDefective: false,
    };
  }
}
